import type { i18n as I18nInstance } from 'i18next';
import { i18n } from '/shared/translations/i18n';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';
import tankerIcon from '/business/shared/images/icons/bp-road-tanker-med-pictograms-bp-green-.svg';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { QuestionConfigQuestionType } from '/index';
import formatNumber from '/shared/functions/formatNumber';

import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Downstream transportation card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Downstream transportation card image@2x.jpg';

import educationContentJson from '/business/shared/components/questions/scope3/transportAndDistribution/downstream/_education/downstreamTransportationAndDistribution.json';
import useGetTransportationAndDistributionSchema from '/business/shared/components/questions/scope3/transportAndDistribution/downstream/useGetTransportationAndDistributionSchema';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import useFormatDownstreamTransportationSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatDownstreamTransportationSupplier';
import useFormatDownstreamTransportationCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatDownstreamTransportationCustom';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import transportVehicleTypeLabel from '/business/shared/util/transportVehicleTypeLabel';
import { getTranslatedDisplayName } from '/business/shared/components/questions/helper/internals/mappedFieldValues';

const carVanTruckActivitySubheader = (
  <>
    <StyledParagraph $largeText $hasExtraMarginBottom>
      Enter the amount spent in each of the following categories during the reporting period: <ReportingPeriod $green />
    </StyledParagraph>
    <StyledInfoFlash>
      <div>For more information, refer to the "Help" section for detailed guidance.</div>
    </StyledInfoFlash>
  </>
);

export const getConfigureDownstreamTransportationAndDistribution = (
  _options: { i18n?: I18nInstance } = {},
): QuestionConfigQuestionType => {
  const downstreamTransportationTitle = i18n.t(
    'business:QuestionPage.forms.emissionSelector.downstreamTransportationAndDistribution',
    'Downstream transportation and distribution',
  );
  const carsLabel = i18n.t('business:QuestionPage.forms.vehicleTypeSelector.para2', 'Cars');
  const vansLabel = i18n.t('business:QuestionPage.forms.vehicleTypeSelector.para3', 'Vans');
  const trucksLabel = i18n.t('business:QuestionPage.forms.vehicleTypeSelector.para4', 'Trucks');
  const activitySubquestions = {
    COMPANY_CARS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'cars',
      title: `${downstreamTransportationTitle} - ${carsLabel}`,
      breadcrumbTitle: 'Cars',
      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      apiKey: 'calculation-downstream-distrib-activity-car',
      useHookLoader: useGetTransportationAndDistributionSchema,
      table: {
        title: 'Calculated carbon emissions for activity data: ',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: [
          'calculation-downstream-distrib-activity-truck',
          'calculation-downstream-distrib-activity-van',
        ],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for downstream transportation and distribution: ',
        supplier: {
          columns: ['supplierName', 'supplierIntensityDataOrAbsoluteEmissions', 'totalDistance', 'carbonEmissions'],
        },
        custom: {
          columns: ['supplierName', 'intensityValue', 'unitOfMeasurement', 'totalDistance', 'carbonEmissions'],
        },
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
    COMPANY_VANS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'vans',
      title: `${downstreamTransportationTitle} - ${vansLabel}`,
      breadcrumbTitle: 'Vans',
      apiKey: 'calculation-downstream-distrib-activity-van',
      useHookLoader: useGetTransportationAndDistributionSchema,
      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion2Leased.table.title'),
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: [
          'calculation-downstream-distrib-activity-truck',
          'calculation-downstream-distrib-activity-car',
        ],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for downstream transportation and distribution: ',
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
    COMPANY_TRUCKS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'trucks',
      title: `${downstreamTransportationTitle} - ${trucksLabel}`,
      breadcrumbTitle: 'Trucks',
      apiKey: 'calculation-downstream-distrib-activity-truck',
      useHookLoader: useGetTransportationAndDistributionSchema,
      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion2Leased.table.title'),
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: [
          'calculation-downstream-distrib-activity-van',
          'calculation-downstream-distrib-activity-car',
        ],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for downstream transportation and distribution: ',
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
  };
  return {
    title: 'Downstream transportation and distribution',
    id: 'downstream-transportation-and-distribution',
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamTransportationAndDistribution',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamTransportationAndDistributionTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamTransportationAndDistribution',
    form: import(
      '/business/shared/components/questions/scope3/transportAndDistribution/shared/TransportAndDistributionVehSelector'
    ),
    scopeCategory: questionScopeCategories.SCOPE_3,
    subQuestions: [
      activitySubquestions.COMPANY_CARS,
      activitySubquestions.COMPANY_VANS,
      activitySubquestions.COMPANY_TRUCKS,
    ],
    icon: tankerIcon,
    isCompletePropName: 'isDownstreamTransportationAndDistributionComplete',
    selector: {
      questionBaseRootApiKey: 'calculation-downstream-distrib-activity-car',
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: (
        <StyledParagraph $largeText>
          To calculate emissions associated with any inbound or outbound logistics services{' '}
          <b>paid for and operated by other businesses or individuals</b>, you can use a combination of activity data,
          supplier data, and custom data.
        </StyledParagraph>
      ),
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Supplier ID, type of fuel, vehicle size, total distance traveled.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Customer/supplier name, transportation supplier ID, supplier intensity data/absolute emissions, total distance traveled.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData:
            'Customer/supplier name, transportation supplier ID, intensity data/absolute emissions, total distance traveled.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatDownstreamTransportationSupplier({
        questionFormatName: 'downstream-transportation-and-distribution',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatDownstreamTransportationCustom({ questionFormatName: 'downstream-transportation-and-distribution' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the details for any inbound or outbound logistics services{' '}
          <b>paid for and operated by other businesses or individuals</b> during the reporting period{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the amount spent in each of the following categories during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
